import React, { useState, useEffect } from 'react';
import { Bell, Send, Clock, Grid as GridIcon, Layers as LayersIcon } from 'lucide-react';
import { NotificationSettingsResponse, NotificationSettingsPayload, Device, AppCatalogItem } from '../types';
import { getDevices, getNotificationSettings, updateNotificationSettings, pollNow, listAppsAll } from '../services/api';
import { toast } from 'react-hot-toast';
import { config } from '../config';
import { recordNotification } from '../utils/notificationHistory';
import { getFlagUrl, flagSrc } from '../utils/flags';

const buildDefaultNotif = (): NotificationSettingsResponse => ({
  id: -1,
  user_id: -1,
  enabled: false,
  sound_enabled: true,
  web_toast_enabled: true as unknown as any,
  selected_app_ids: [],
  selected_device_ids: [],
  poll_interval_minutes: 5,
  minute_marks: [],
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  // Telegram
  telegram_enabled: false as unknown as any,
  telegram_sound_enabled: true as unknown as any,
  telegram_proxy_url: undefined,
});

// Список приложений и флаги приходят с backend (таблица app_catalog),
// чтобы можно было добавлять/выключать app_id без правки кода.

const formatKopecks = (amountMinor: number, currency?: string) => {
  const sign = amountMinor < 0 ? '-' : '';
  const abs = Math.abs(Math.trunc(amountMinor || 0));
  const rub = Math.floor(abs / 100);
  const kop = abs % 100;
  return `${sign}${rub}.${String(kop).padStart(2, '0')} ${currency || 'руб.'}`;
};

const proxyAppIcon = (appId?: string) => (appId ? `/api/v1/notifications/app-icon/${appId}` : undefined);
const ensureHttps = (url?: string) => (url ? url.replace('http://', 'https://') : undefined);
const buildDirectLogo = (appId?: string) => (appId ? `https://app.rewardguru.limited/api-5.0/app_logo?app_id=${appId}&img_size=Logo2` : undefined);

const INTERVAL_OPTIONS = [1,5,10,15,20,25,30,35,40,45,50,55];
const MINUTE_CHOICES = [0,5,10,15,20,25,30,35,40,45,50,55];

const getIconObjectUrl = async (rawUrl?: string, appId?: string) => {
  const url = rawUrl || buildDirectLogo(appId);
  if (!url) return undefined;
  try {
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('access_token');
    if (token && url.startsWith('/')) headers['Authorization'] = `Bearer ${token}`;
    let resp = await fetch(url, { cache: 'no-cache', headers });
    if (!resp.ok && appId) {
      const direct = buildDirectLogo(appId)!;
      resp = await fetch(direct, { cache: 'no-cache' });
    }
    if (!resp.ok) return url;
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    return objUrl;
  } catch {
    return url;
  }
};

const Settings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [notif, setNotif] = useState<NotificationSettingsResponse | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [apps, setApps] = useState<AppCatalogItem[]>([]);
  const [savingNotif, setSavingNotif] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);
  const [tgOpen, setTgOpen] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);

  useEffect(() => {
    loadNotif();
    loadUserDevices();
    loadApps();
  }, []);

  const loadNotif = async () => {
    try {
      const s = await getNotificationSettings();
      // Нормализуем app_id, чтобы сравнения/чекбоксы работали стабильно
      const norm = {
        ...s,
        selected_app_ids: (s.selected_app_ids || []).map((x) => String(x || '').trim().toUpperCase()).filter(Boolean),
      } as NotificationSettingsResponse;
      setNotif(norm);
      setNotifOpen(!!s?.enabled);
    } catch (e) {
      console.error('Ошибка загрузки настроек уведомлений', e);
      toast.error('Не удалось загрузить настройки уведомлений');
      setNotif(buildDefaultNotif());
      setNotifOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const loadUserDevices = async () => {
    try {
      const list = await getDevices();
      setDevices(list || []);
    } catch (e) {
      console.error('Ошибка загрузки устройств', e);
      toast.error('Не удалось загрузить устройства');
      setDevices([]);
    }
  };

  const loadApps = async () => {
    try {
      const list = await listAppsAll();
      setApps(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Ошибка загрузки списка приложений', e);
      setApps([]);
    }
  };

  const requestNotifyPermission = async () => {
    try {
      if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
        await Notification.requestPermission();
      }
    } catch {}
  };

  const playSound = async () => {
    const bundled = new URL('../assets/notify.mp3', import.meta.url).href;
    const configured = config.getNotificationSoundUrl();
    const candidates = [bundled, configured];
    for (const url of candidates) {
      try {
        const audio = new Audio(url);
        audio.volume = 1;
        await audio.play();
        return;
      } catch {}
    }
    try {
      const ctx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 880;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
      o.start();
      setTimeout(() => {
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
        o.stop(ctx.currentTime + 0.25);
      }, 200);
    } catch {}
  };

  const notifyBrowser = (title: string, body: string, icon?: string) => {
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification(title, { body, icon });
      }
    } catch {}
  };

  const quickSave = async (next: NotificationSettingsResponse) => {
    setNotif(next);
    const payload: NotificationSettingsPayload = {
      enabled: next.enabled,
      sound_enabled: next.sound_enabled,
      web_toast_enabled: (next as any).web_toast_enabled ?? true,
      selected_app_ids: next.selected_app_ids || [],
      selected_device_ids: next.selected_device_ids || [],
      poll_interval_minutes: next.poll_interval_minutes || 5,
      minute_marks: next.minute_marks || [],
      telegram_enabled: (next as any).telegram_enabled ?? false,
      telegram_bot_token: (next as any).telegram_bot_token ?? undefined,
      telegram_proxy_url: (next as any).telegram_proxy_url ?? undefined,
      telegram_sound_enabled: (next as any).telegram_sound_enabled ?? true,
    };
    try {
      const res = await updateNotificationSettings(payload);
      setNotif(res);
      toast.success('Настройки сохранены');
    } catch (e) {
      toast.error('Не удалось сохранить');
    }
  };

  // Оставляем только устройства с заполненным hash_get_new_orders
  const selectableDevices = (devices || []).filter((d) => !!(d as any).hash_get_new_orders && String((d as any).hash_get_new_orders).trim().length > 0);

  if (loading && !notif) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="px-8 py-8 bg-[#181A20] min-h-screen">
      <div className="mx-auto">
        <h1 className="text-3xl font-bold text-white tracking-wide mb-8">
          Настройки
        </h1>

        {/* Уведомления о новых заданиях */}
        <div className="bg-[#181A20] rounded-xl shadow border border-[#23262F]">
          <div className="px-6 py-4 border-b border-[#23262F] flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-white">
                Уведомления о новых заданиях
              </h2>
              <p className="text-sm text-[#B1B5C3]">Выбор приложений и устройств для отслеживания</p>
          </div>
            <div className="flex items-center space-x-2">
              <span className="text-white text-sm hidden sm:inline">Включить уведомления</span>
              <button
                type="button"
                role="switch"
                aria-checked={!!notif?.enabled}
                onClick={async () => {
                  const base = notif ?? buildDefaultNotif();
                  const next = { ...base, enabled: !base.enabled } as NotificationSettingsResponse;
                  if (next.enabled) await requestNotifyPermission();
                  setNotifOpen(!!next.enabled);
                  await quickSave(next);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${notif?.enabled ? 'bg-blue-600' : 'bg-[#353945]'}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${notif?.enabled ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
          </div>
              </div>
          {notifOpen && (
          <div className="p-6 space-y-6">
            <button
              type="button"
              onClick={() => setSiteOpen((v) => !v)}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-start gap-2">
                <Bell size={16} className="text-[#B1B5C3]" />
                <h3 className="text-md font-semibold text-white text-left whitespace-normal break-words leading-snug">Отображение и звук уведомлений на сайте</h3>
              </div>
              <span className={`transition-transform ${siteOpen ? 'rotate-180' : ''}`}>
                <svg className="w-4 h-4 text-[#B1B5C3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
              </span>
            </button>
            {siteOpen && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center space-x-2">
                  <span className="text-white">Показывать пуш-уведомления</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!(notif as any)?.web_toast_enabled}
                    onClick={async () => {
                      const base = notif ?? buildDefaultNotif();
                      const next = { ...base, web_toast_enabled: !(base as any).web_toast_enabled } as any;
                      await quickSave(next as NotificationSettingsResponse);
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${(notif as any)?.web_toast_enabled ? 'bg-blue-600' : 'bg-[#353945]'}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${(notif as any)?.web_toast_enabled ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                  </div>
                <div className="flex items-center space-x-2">
                  <span className="text-white">Звук уведомления</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!notif?.sound_enabled}
                    onClick={async () => {
                      const base = notif ?? buildDefaultNotif();
                      const next = { ...base, sound_enabled: !base.sound_enabled } as NotificationSettingsResponse;
                      await quickSave(next);
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${notif?.sound_enabled ? 'bg-blue-600' : 'bg-[#353945]'}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${notif?.sound_enabled ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>
          </div>
              <button
                type="button"
                onClick={async () => {
                  await playSound();
                  toast.success('Звук проигран');
                }}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 w-full sm:w-auto"
              >
                Тест звука
              </button>
          </div>
        )}

            {/* Telegram */}
                  <div>
              <button
                type="button"
                onClick={() => setTgOpen((v) => !v)}
                className="w-full flex items-center justify-between mb-3"
              >
                <div className="flex items-start gap-2">
                  <Send size={16} className="text-[#B1B5C3]" />
                  <h3 className="text-md font-semibold text-white text-left whitespace-normal break-words leading-snug">Telegram уведомления</h3>
                  </div>
                <span className={`transition-transform ${tgOpen ? 'rotate-180' : ''}`}>
                  <svg className="w-4 h-4 text-[#B1B5C3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </span>
              </button>
              {tgOpen && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-white">Включить уведомления</span>
                      <button
                        type="button"
                      role="switch"
                      aria-checked={!!(notif as any)?.telegram_enabled}
                      onClick={async () => {
                        const base = notif ?? buildDefaultNotif();
                        const next = { ...base, telegram_enabled: !(base as any).telegram_enabled } as any;
                        await quickSave(next as NotificationSettingsResponse);
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${(notif as any)?.telegram_enabled ? 'bg-blue-600' : 'bg-[#353945]'}`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${(notif as any)?.telegram_enabled ? 'translate-x-6' : 'translate-x-1'}`}
                      />
                      </button>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-white">Звук в Telegram</span>
                        <button
                      type="button"
                      role="switch"
                      aria-checked={!!(notif as any)?.telegram_sound_enabled}
                      onClick={async () => {
                        const base = notif ?? buildDefaultNotif();
                        const next = { ...base, telegram_sound_enabled: !(base as any).telegram_sound_enabled } as any;
                        await quickSave(next as NotificationSettingsResponse);
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${(notif as any)?.telegram_sound_enabled ? 'bg-blue-600' : 'bg-[#353945]'}`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${(notif as any)?.telegram_sound_enabled ? 'translate-x-6' : 'translate-x-1'}`}
                      />
                        </button>
                  </div>
                        <button
                          type="button"
                    onClick={async () => {
                      try {
                        if (notif) await quickSave(notif as any);
                        const { apiService } = await import('../services/api');
                        await apiService.telegramTestMessage();
                        toast.success('Тестовое сообщение отправлено');
                      } catch (e: any) {
                        const msg = e?.response?.data?.detail || 'Ошибка отправки тестового сообщения';
                        toast.error(msg);
                      }
                    }}
                          className="px-3 py-1.5 text-sm font-medium text_WHITE bg-[#353945] rounded-lg hover:bg-[#2A2D36] w-full sm:w-auto"
                  >
                    Тест Telegram
                        </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-[#B1B5C3] mb-1">Bot Token</label>
                    <input
                      type="text"
                      value={(notif as any)?.telegram_bot_token || ''}
                      onChange={(e) => setNotif({ ...(notif ?? buildDefaultNotif()), telegram_bot_token: e.target.value } as any)}
                      placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                      className="w-full px-3 py-2 bg-[#181A20] border border-[#353945] rounded-lg text_WHITE placeholder-[#777E90] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#B1B5C3] mb-1">Telegram Proxy (SOCKS5h URL)</label>
                    <input
                      type="text"
                      value={(notif as any)?.telegram_proxy_url || ''}
                      onChange={(e) => setNotif({ ...(notif ?? buildDefaultNotif()), telegram_proxy_url: e.target.value } as any)}
                      placeholder="socks5h://user:pass@host:port"
                      className="w-full px-3 py-2 bg-[#181A20] border border-[#353945] rounded-lg text_WHITE placeholder-[#777E90] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    />
                  </div>
              
            </div>
          </div>
              )}
                  </div>

            {/* Настройки расписания */}
            <button
              type="button"
              onClick={() => setSchedOpen((v) => !v)}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-start gap-2">
                <Clock size={16} className="text-[#B1B5C3]" />
                <h3 className="text-md font-semibold text-white text-left whitespace-normal break-words leading-snug">Расписание проверки новых заданий</h3>
              </div>
              <span className={`transition-transform ${schedOpen ? 'rotate-180' : ''}`}>
                <svg className="w-4 h-4 text-[#B1B5C3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </span>
            </button>
            {schedOpen && (
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <label className="text-sm text-[#B1B5C3]">Интервал (мин):</label>
                <select
                  value={notif?.poll_interval_minutes || 5}
                  onChange={(e) => notif && setNotif({ ...notif, poll_interval_minutes: parseInt(e.target.value, 10) })}
                  className="px-3 py-2 bg-[#181A20] border border-[#353945] rounded-lg text-white w-full sm:w-44"
                >
                  {INTERVAL_OPTIONS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                  </div>
                  <div>
                <div className="text-sm text-[#B1B5C3] mb-2">Конкретные минуты (локальное время):</div>
                <div className="flex flex-wrap gap-2">
                  {MINUTE_CHOICES.map((m) => {
                    const checked = !!notif?.minute_marks?.includes(m);
                    return (
                      <label key={m} className={`px-2 py-1 rounded border cursor-pointer ${checked ? 'border-blue-500 bg-blue-500/10' : 'border-[#353945] bg-[#23262F]'}`}>
                    <input
                          type="checkbox"
                          className="hidden"
                          checked={checked}
                          onChange={(e) => {
                            if (!notif) return;
                            const set = new Set(notif.minute_marks || []);
                            if (e.target.checked) set.add(m); else set.delete(m);
                            setNotif({ ...notif, minute_marks: Array.from(set).sort((a,b)=>a-b) });
                          }}
                        />
                        <span className="text-white text-sm">{m.toString().padStart(2,'0')}</span>
                    </label>
                    );
                  })}
                </div>
                <div className="text-xs text-[#B1B5C3] mt-1">Если выбран список минут — опрос идёт ровно по этим минутам каждого часа; иначе — по интервалу.</div>
                  </div>
                  </div>
            )}

            {/* Список приложений */}
                  <div>
              <button
                type="button"
                onClick={() => setAppsOpen((v) => !v)}
                className="w-full flex items-center justify-between mb-3"
              >
                <div className="flex items-start gap-2">
                  <GridIcon size={16} className="text-[#B1B5C3]" />
                  <h3 className="text-md font-semibold text-white text-left whitespace-normal break-words leading-snug">Приложения для отслеживания</h3>
                  </div>
                <span className={`transition-transform ${appsOpen ? 'rotate-180' : ''}`}>
                  <svg className="w-4 h-4 text-[#B1B5C3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </span>
              </button>
              {appsOpen && (() => {
                const all = Array.isArray(apps) ? apps : [];
                const enabled = all.filter((a) => !!a?.enabled);
                const enabledSet = new Set(enabled.map((a) => String(a.app_id || '').toUpperCase()).filter(Boolean));
                const selected = (notif?.selected_app_ids || []).map((x) => String(x || '').toUpperCase()).filter(Boolean);
                const hiddenSelected = selected.filter((id) => !enabledSet.has(id));
                const byId = new Map(all.map((a) => [String(a.app_id || '').toUpperCase(), a]));
                return (
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {enabled.map((a) => {
                        const appId = String(a.app_id || '').toUpperCase();
                        const checked = !!notif?.selected_app_ids?.includes(appId);
                        const cc = (a.country_code || '').toLowerCase();
                        return (
                          <label key={appId} className={`relative w-20 h-20 rounded-lg border ${checked ? 'border-blue-500 bg-blue-500/10' : 'border-[#353945] bg-[#23262F]'} cursor-pointer flex items-center justify-center`}>
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={checked}
                              onChange={(e) => {
                                const base = notif ?? buildDefaultNotif();
                                const set = new Set((base.selected_app_ids || []).map((x) => String(x || '').toUpperCase()).filter(Boolean));
                                if (e.target.checked) set.add(appId); else set.delete(appId);
                                setNotif({ ...base, selected_app_ids: Array.from(set) });
                              }}
                            />
                            {cc && cc.length === 2 ? (
                              <img src={flagSrc(cc)} alt="" className="absolute top-1 left-1 w-5 h-4 rounded-sm border border-[#23262F] bg-[#181A20]" />
                            ) : null}
                            <img
                              src={`https://app.rewardguru.limited/api-5.0/app_logo?app_id=${appId}&img_size=Logo2`}
                              alt="app"
                              className="w-10 h-10 object-contain"
                            />
                          </label>
                        );
                      })}
                    </div>

                    {hiddenSelected.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-xs text-[#B1B5C3]">Скрытые (отключены админом), но сейчас выбраны:</div>
                        <div className="flex flex-wrap gap-2">
                          {hiddenSelected.map((appId) => {
                            const a = byId.get(appId);
                            const cc = (a?.country_code || '').toLowerCase();
                            const checked = true;
                            return (
                              <label key={appId} className={`relative w-20 h-20 rounded-lg border border-[#353945] bg-[#23262F] cursor-pointer flex items-center justify-center opacity-60`}>
                                <input
                                  type="checkbox"
                                  className="hidden"
                                  checked={checked}
                                  onChange={(e) => {
                                    const base = notif ?? buildDefaultNotif();
                                    const set = new Set((base.selected_app_ids || []).map((x) => String(x || '').toUpperCase()).filter(Boolean));
                                    if (e.target.checked) set.add(appId); else set.delete(appId);
                                    setNotif({ ...base, selected_app_ids: Array.from(set) });
                                  }}
                                />
                                {cc && cc.length === 2 ? (
                                  <img src={flagSrc(cc)} alt="" className="absolute top-1 left-1 w-5 h-4 rounded-sm border border-[#23262F] bg-[#181A20]" />
                                ) : null}
                                <img
                                  src={`https://app.rewardguru.limited/api-5.0/app_logo?app_id=${appId}&img_size=Logo2`}
                                  alt="app"
                                  className="w-10 h-10 object-contain"
                                />
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })()}
                </div>
            {/* Список устройств */}
                  <div>
              <button
                type="button"
                onClick={() => setDevicesOpen((v) => !v)}
                className="w-full flex items-center justify-between mb-3"
              >
                <div className="flex items-start gap-2">
                  <LayersIcon size={16} className="text-[#B1B5C3]" />
                  <h3 className="text-md font-semibold text-white text-left whitespace-normal break-words leading-snug">Устройства для отслеживания</h3>
                </div>
                <span className={`transition-transform ${devicesOpen ? 'rotate-180' : ''}`}>
                  <svg className="w-4 h-4 text-[#B1B5C3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </span>
              </button>
              {devicesOpen && (
              <>
              {selectableDevices.length === 0 ? (
                <div className="text-sm text-[#B1B5C3]">Нет устройств с заполненным Hash getNewOrders</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {selectableDevices.map((d) => {
                    const checked = !!notif?.selected_device_ids?.includes(d.id);
                    return (
                      <label key={d.id} className="relative flex items-center p-2 rounded-lg border border-[#353945] bg-[#23262F] cursor-pointer hover:border-[#4B4F5D]">
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={checked}
                          onChange={(e) => {
                            const base = notif ?? buildDefaultNotif();
                            const set = new Set(base.selected_device_ids || []);
                            if (e.target.checked) set.add(d.id); else set.delete(d.id);
                            setNotif({ ...base, selected_device_ids: Array.from(set) });
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-medium truncate">{d.name}</div>
                          <div className="text-xs text-[#B1B5C3] truncate">{d.model}</div>
                        </div>
                        {checked ? (
                          <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-5 h-5 rounded bg-blue-600 text-white text-xs">✓</span>
                        ) : null}
                      </label>
                    );
                  })}
              </div>
                  )}
              </>
              )}
            </div>

            <div className="flex items-center space-x-3">
                  <button
                    type="button"
                disabled={!notif || savingNotif}
                onClick={async () => {
                  if (!notif) return;
                  setSavingNotif(true);
                  const payload: NotificationSettingsPayload = {
                    enabled: notif.enabled,
                    sound_enabled: notif.sound_enabled,
                    web_toast_enabled: (notif as any).web_toast_enabled ?? true,
                    selected_app_ids: notif.selected_app_ids || [],
                    selected_device_ids: notif.selected_device_ids || [],
                    poll_interval_minutes: notif.poll_interval_minutes || 5,
                    minute_marks: notif.minute_marks || [],
                    telegram_enabled: notif.telegram_enabled,
                    telegram_bot_token: notif.telegram_bot_token,
                    telegram_proxy_url: (notif as any).telegram_proxy_url ?? undefined,
                    telegram_sound_enabled: notif.telegram_sound_enabled,
                  };
                  try {
                    const res = await updateNotificationSettings(payload);
                    setNotif(res);
                    toast.success('Настройки сохранены');
                  } catch (e: any) {
                    toast.error('Не удалось сохранить');
                  } finally {
                    setSavingNotif(false);
                  }
                }}
                className={`px-6 py-3 text-sm font-medium text-white rounded-lg ${savingNotif ? 'bg-gray-600' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {savingNotif ? 'Сохранение...' : 'Сохранить'}
                  </button>
                  <button
                    type="button"
                onClick={async () => {
                  try {
                    await requestNotifyPermission();
                    if (notif) {
                      const payload: NotificationSettingsPayload = {
                        enabled: notif.enabled,
                        sound_enabled: notif.sound_enabled,
                        web_toast_enabled: (notif as any).web_toast_enabled ?? true,
                        selected_app_ids: notif.selected_app_ids || [],
                        selected_device_ids: notif.selected_device_ids || [],
                        poll_interval_minutes: notif.poll_interval_minutes || 5,
                        minute_marks: notif.minute_marks || [],
                        telegram_enabled: (notif as any).telegram_enabled,
                        telegram_bot_token: (notif as any).telegram_bot_token,
                        telegram_proxy_url: (notif as any).telegram_proxy_url ?? undefined,
                        telegram_sound_enabled: (notif as any).telegram_sound_enabled,
                      };
                      try {
                        const saved = await updateNotificationSettings(payload);
                        setNotif(saved);
                      } catch {}
                    }

                    const res = await pollNow();
                    if (res?.success && Array.isArray(res.groups) && res.groups.length > 0) {
                      let anyShown = false;
                      for (const g of res.groups) {
                        const formatted = formatKopecks(Math.round(g.total_price || 0), '');
                        const title = '🔥 Новый заказ!';
                        const flagUrl = getFlagUrl(g.app_id);
                        const body = `Количество: ${g.devices_count}\nОбщая сумма: ${formatted} 💰`;
                        // Меняем приоритет: сначала внешний прямой URL, чтобы не бить ngrok
                        const rawImg = ensureHttps(g.sample_pic) || buildDirectLogo(g.app_id) || proxyAppIcon(g.app_id);
                        const icon = await getIconObjectUrl(rawImg, g.app_id);

                        notifyBrowser(title, body, icon);
                        if ((notif as any)?.web_toast_enabled ?? true) {
                          toast.custom(
                            <div className="flex items-center p-3 bg-[#23262F] border border-[#353945] rounded-lg shadow-lg max-w-sm">
                              {icon ? (
                                <img src={icon} alt="app" className="w-12 h-12 rounded mr-3 object-contain" />
                              ) : null}
                              <div className="text-sm whitespace-pre-line">
                                <div className="text-white font-semibold flex items-center gap-2">
                                  <span>{title}</span>
                                  {flagUrl ? <img src={flagUrl} alt="flag" className="w-4 h-3 rounded-sm" /> : null}
                                </div>
                                <div className="text-[#B1B5C3]">{body}</div>
                              </div>
                            </div>,
                            { duration: 6000 }
                          );
                        }
                        anyShown = true;

                        try {
                          await recordNotification({
                            app_id: g.app_id,
                            title,
                            body,
                            devices_count: g.devices_count,
                            total_price_minor: Math.round(g.total_price || 0),
                          });
                          // Не триггерим полную перезагрузку истории
                        } catch {}
                      }

                      if (anyShown && notif?.sound_enabled) await playSound();
                    } else {
                      toast('Новых заданий нет');
                    }
                  } catch (e) {
                    console.error(e);
                    toast.error('Не удалось проверить сейчас');
                  }
                }}
                className="px-6 py-3 text-sm font-medium text-white bg-[#353945] rounded-lg hover:bg-[#2A2D36]"
              >
                Проверить сейчас
                  </button>
                </div>
              </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings; 